import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles, SkipForward, Moon, Square, CheckSquare } from 'lucide-react';
import { TutorialStep, Language } from '../types';

interface OnboardingTutorialProps {
  lang: Language;
  onComplete: () => void;
}

const STEPS: Record<Language, TutorialStep[]> = {
  en: [
    { id: 'start', targetId: 'logo', title: 'Home', description: 'This logo takes you back to the top of the page anytime.' },
    { id: 'lang', targetId: 'header-lang-btn', title: 'Language', description: 'Switch the website to your preferred language here.' },
    { id: 'signin', targetId: 'header-portal', title: 'Account', description: 'Sign in here to see your saved astrology charts and messages.' },
    { id: 'book-h', targetId: 'header-book-btn', title: 'Book Now', description: 'Use this button to schedule a private reading with Allie.' },
    { id: 'hero-c', targetId: 'hero-btn-chart', title: 'Free Chart', description: 'Get a quick, free look at your birth chart right now.' },
    { id: 'services', targetId: 'services-title', title: 'Our Readings', description: 'Browse the different types of astrology sessions Allie offers.' },
    { id: 'about', targetId: 'about-title', title: 'About Allie', description: 'Learn about Allie\'s experience and her approach to astrology.' },
    { id: 'footer-info', targetId: 'logo-footer', title: 'Ancestral Links', description: 'Access legal disclaimers, social links, and the admin gate here in the footer.' },
  ],
  es: [
    { id: 'start', targetId: 'logo', title: 'Inicio', description: 'Vuelve al inicio de la página.' },
    { id: 'services', targetId: 'services-title', title: 'Servicios', description: 'Explora nuestras lecturas.' },
  ],
  zh: [{ id: 'start', targetId: 'logo', title: '首页', description: '点击返回顶部。' }],
  ja: [{ id: 'start', targetId: 'logo', title: 'ホーム', description: 'トップに戻ります。' }],
  ru: [{ id: 'start', targetId: 'logo', title: 'Главная', description: 'Вернуться наверх.' }],
  de: [{ id: 'start', targetId: 'logo', title: 'Startseite', description: 'Zurück nach oben.' }]
};

const OnboardingTutorial: React.FC<OnboardingTutorialProps> = ({ lang, onComplete }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const steps = STEPS[lang] || STEPS.en;
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let isMounted = true;
    const updatePosition = () => {
      const el = document.getElementById(steps[currentIdx].targetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        const timer = setTimeout(() => {
          if (isMounted) {
            setTargetRect(el.getBoundingClientRect());
          }
        }, 1200); 
        return () => clearTimeout(timer);
      }
    };
    
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => {
      isMounted = false;
      window.removeEventListener('resize', updatePosition);
    };
  }, [currentIdx, steps]);

  const handleFinish = () => {
    if (dontShowAgain) {
      localStorage.setItem('allie_skip_tutorial', 'true');
    }
    onComplete();
  };

  const handleNext = () => {
    if (currentIdx < steps.length - 1) {
      setCurrentIdx(currentIdx + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrev = () => {
    if (currentIdx > 0) {
      setCurrentIdx(currentIdx - 1);
    }
  };

  if (!targetRect) return null;

  const getCardPosition = () => {
    const gutter = 32;
    const cardWidth = 300;
    const cardHeight = 220; // Extra height for the checkbox
    let top = targetRect.bottom + gutter;
    let left = targetRect.left + (targetRect.width / 2) - (cardWidth / 2);

    if (top + cardHeight > window.innerHeight) {
      top = targetRect.top - cardHeight - gutter;
    }

    left = Math.max(gutter, Math.min(window.innerWidth - cardWidth - gutter, left));
    
    return { top, left };
  };

  const { top, left } = getCardPosition();

  return (
    <div className="fixed inset-0 z-[2000000] pointer-events-none animate-fade-in">
      <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-[1px] transition-all duration-300" />
      
      <div 
        className="absolute transition-all duration-500 ease-out-expo border-2 border-amber-500 shadow-[0_0_0_9999px_rgba(2,6,23,0.35),_inset_0_0_20px_rgba(245,158,11,0.5)] rounded-sm"
        style={{
          top: targetRect.top - 16,
          left: targetRect.left - 16,
          width: targetRect.width + 32,
          height: targetRect.height + 32,
        }}
      >
        <div className="absolute inset-[-4px] border border-amber-500/20 rounded-sm animate-pulse" />
      </div>

      <div 
        ref={cardRef}
        className="absolute z-10 w-[300px] bg-slate-900 border border-amber-500/20 p-6 rounded-sm shadow-[0_25px_60px_rgba(0,0,0,0.8)] pointer-events-auto transition-all duration-500 ease-out-expo"
        style={{ top, left }}
      >
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-amber-500" />
            <h4 className="text-white text-[10px] uppercase tracking-widest font-bold">
              {steps[currentIdx].title}
            </h4>
          </div>
          <span className="text-[10px] text-indigo-500 font-mono font-bold">
            {currentIdx + 1} / {steps.length}
          </span>
        </div>
        
        <p className="text-sm text-indigo-50 leading-relaxed font-light mb-4 min-h-[3rem]">
          {steps[currentIdx].description}
        </p>

        <div className="w-full h-1 bg-indigo-950 rounded-full overflow-hidden mb-6">
          <div 
            className="h-full bg-amber-500 shadow-[0_0_10px_#f59e0b] transition-all duration-500 ease-out"
            style={{ width: `${((currentIdx + 1) / steps.length) * 100}%` }}
          />
        </div>

        <button 
          onClick={() => setDontShowAgain(!dontShowAgain)}
          className="flex items-center gap-2 mb-6 group transition-all"
        >
          {dontShowAgain ? <CheckSquare size={14} className="text-amber-500" /> : <Square size={14} className="text-indigo-700" />}
          <span className="text-[9px] uppercase tracking-widest font-bold text-indigo-400 group-hover:text-indigo-200">Never show rituals again</span>
        </button>

        <div className="flex items-center justify-between">
          <button onClick={handleFinish} className="text-[9px] uppercase tracking-widest text-indigo-500 hover:text-white flex items-center gap-1 transition-colors">
            <SkipForward size={10}/> Skip
          </button>
          
          <div className="flex gap-3">
            {currentIdx > 0 && (
              <button onClick={handlePrev} className="p-2 border border-indigo-900 text-indigo-400 rounded-sm hover:text-white transition-all">
                <ChevronLeft size={16}/>
              </button>
            )}
             <button onClick={handleNext} className="px-6 py-2 bg-amber-500 text-slate-950 font-bold uppercase tracking-widest text-[10px] rounded-sm hover:bg-amber-400 transition-all flex items-center gap-2 shadow-lg">
               {currentIdx === steps.length - 1 ? 'Start' : 'Next'} <ChevronRight size={16}/>
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTutorial;